"use client";

import type { IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { Counter } from "@tosize/ui";
import { usePathname } from "next/navigation";
import { useMemo } from "react";
import { StyledLink } from "~/components/Link";

interface AccountLink {
	label: string;
	href: string;
	icon: IconDefinition;
	amount?: number;
}

interface AccountLinksClientProps {
	accountLinks: AccountLink[];
}

const ACCOUNT_OVERVIEW_PATH = "/customer/account";
// Regex to match and remove locale prefix (e.g., /nl-nl, /en-nl, /de-at)
// All locales in the app follow the xx-xx format (language-country)
// Uses lookahead (?=\/) to ensure we only match when followed by a path segment
const LOCALE_PREFIX_REGEX = /^\/[a-z]{2}-[a-z]{2}(?:\/|$)/;

export function AccountLinksClient({ accountLinks }: AccountLinksClientProps) {
	const pathname = usePathname();

	// Memoize the normalized pathname to avoid repeated regex operations
	const normalizedPathname = useMemo(() => {
		return pathname.replace(LOCALE_PREFIX_REGEX, "");
	}, [pathname]);

	const isActive = (href: string) => {
		// Exact match for the account overview page
		if (href === ACCOUNT_OVERVIEW_PATH) {
			return normalizedPathname === ACCOUNT_OVERVIEW_PATH;
		}

		// For other pages, check for exact match or boundary (next char is '/')
		return (
			normalizedPathname === href ||
			(normalizedPathname.startsWith(href) &&
				normalizedPathname.length > href.length &&
				normalizedPathname.charAt(href.length) === "/")
		);
	};

	return (
		<>
			{accountLinks.map((item) => (
				<div
					key={item.href}
					className="flex snap-center items-center gap-2 whitespace-nowrap py-2 leading-none"
				>
					<StyledLink
						href={item.href}
						starticon={item.icon}
						iconColor="dark"
						iconSize="default"
						className={isActive(item.href) ? "font-bold" : ""}
					>
						{item.label}
					</StyledLink>

					{!!item.amount && <Counter amount={item.amount} color="secondary" />}
				</div>
			))}
		</>
	);
}
